import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { BookOpenText, Loader2, Sparkles, Trash2, Upload, Wand2, Music4, ArrowRight, Globe2, Drum } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AgentsGrid from "@/components/AgentsGrid";
import ScenePlan from "@/components/ScenePlan";

const ProviderToggle = ({ value, onChange, testId }) => (
  <button type="button" onClick={() => onChange(value === "openai" ? "claude" : "openai")} className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-primary/15 text-primary border border-primary/30" data-testid={testId} title="Switch AI model">{value === "openai" ? "ChatGPT" : "Claude"}</button>
);

const EXAMPLE = `ACT 1 — Dawn over the village. Hana, a young archer, trains alone in the mist, serene.\nA bell tolls: riders in black armour approach. Villagers flee; Hana hides her little sister.\nACT 2 — Lord Oda enters the temple, menacing. Fierce fight between Hana and the guards. She is wounded and collapses while the village burns.\nACT 3 — In spring Hana returns with the Northern army. Final assault against Oda on the mountain summit — a shining victory as cherry trees bloom.`;

export default function Narrative() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [opts, setOpts] = useState(null);
  const [script, setScript] = useState("");
  const [title, setTitle] = useState("");
  const [provider, setProvider] = useState("claude");
  const [culture, setCulture] = useState("auto");
  const [ensemble, setEnsemble] = useState("auto");
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState([]);
  const fileRef = useRef(null);

  const load = useCallback(() => api.get("/narrative").then(({ data }) => setItems(data)).catch(() => {}), []);
  useEffect(() => { api.get("/narrative/options").then(({ data }) => setOpts(data)).catch(() => {}); load(); }, [load]);

  const importFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const text = await f.text();
    setScript(text.slice(0, 8000));
    setTitle((t) => t || f.name.replace(/\.[^.]+$/, "").slice(0, 60));
    e.target.value = "";
  };

  const analyze = async () => {
    if (script.trim().length < 20) return toast.error("Paste at least a few sentences of scenario.");
    setBusy(true);
    try {
      const body = { script, title, provider, culture: culture === "auto" ? null : culture, ensemble: ensemble === "auto" ? null : ensemble };
      const { data } = await api.post("/narrative/analyze", body);
      toast.success(`${data.scenes.length} scenes detected — review the plan, then build the theme.`);
      setItems((l) => [data, ...l]);
    } catch (e) { toast.error(e.response?.data?.detail || "Analysis failed."); }
    finally { setBusy(false); }
  };

  const update = async (id, patch) => {
    try { const { data } = await api.patch(`/narrative/${id}`, patch); setItems((l) => l.map((n) => (n.id === id ? data : n))); }
    catch (e) { toast.error(e.response?.data?.detail || "Update failed."); }
  };
  const build = async (n) => {
    try {
      const { data } = await api.post(`/narrative/${n.id}/build`);
      toast.success(`Theme queued (${data.credits} credits) with ${data.cues.length} narrative cues.`);
      refreshUser?.(); await load();
      navigate("/dashboard/themes");
    } catch (e) { toast.error(e.response?.data?.detail || "Build failed."); }
  };
  const remove = async (n) => {
    if (!window.confirm(`Delete “${n.title}”? The theme, if built, stays in Themes.`)) return;
    try { await api.delete(`/narrative/${n.id}`); setItems((l) => l.filter((x) => x.id !== n.id)); } catch { toast.error("Delete failed."); }
  };

  return (
    <div className="p-6 lg:p-8 max-w-6xl space-y-8" data-testid="narrative-page">
      <header>
        <h1 className="font-display text-4xl font-extrabold flex items-center gap-3"><BookOpenText className="text-primary" /> NarrativeSync</h1>
        <p className="text-muted-foreground mt-1">Paste a scenario — the AI segments it into scenes, maps each emotion to musical parameters (MoodMapper), applies a cultural colour (CulturalLens) and builds a leitmotif theme with a ready-to-play cue sheet.</p>
      </header>

      <section className="rounded-2xl border border-border bg-card p-5 space-y-4" data-testid="narrative-form">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Story title (optional — the AI proposes one)" className="sm:w-80" data-testid="narrative-title" />
          <div className="flex items-center gap-2 flex-wrap">
            <ProviderToggle value={provider} onChange={setProvider} testId="narrative-provider" />
            <input ref={fileRef} type="file" accept=".txt,.fountain,.md" className="hidden" onChange={importFile} data-testid="narrative-import-input" />
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => fileRef.current?.click()} data-testid="narrative-import"><Upload size={14} /> Import .txt / .fountain</Button>
            <Button variant="ghost" size="sm" onClick={() => setScript(EXAMPLE)} data-testid="narrative-example">Example</Button>
          </div>
        </div>
        <Textarea rows={7} value={script} onChange={(e) => setScript(e.target.value.slice(0, 8000))} placeholder="ACT 1 — ... describe the scenes, emotions and turning points of your story or level." data-testid="narrative-script" />
        <div className="grid sm:grid-cols-3 gap-3">
          <label className="block"><span className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold flex items-center gap-1"><Globe2 size={11} /> Cultural lens</span>
            <Select value={culture} onValueChange={setCulture}><SelectTrigger className="mt-1" data-testid="narrative-culture"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="auto">Auto-detect from the story</SelectItem>{opts && Object.entries(opts.cultures).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}{v.instruments.length ? ` · ${v.instruments.slice(0, 2).join(", ")}` : ""}</SelectItem>)}</SelectContent></Select></label>
          <label className="block"><span className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold flex items-center gap-1"><Drum size={11} /> Ensemble (OrchestratorX)</span>
            <Select value={ensemble} onValueChange={setEnsemble}><SelectTrigger className="mt-1" data-testid="narrative-ensemble"><SelectValue /></SelectTrigger>
              <SelectContent>{opts && Object.entries(opts.ensembles).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent></Select></label>
          <div className="flex items-end">
            <Button onClick={analyze} disabled={busy} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 gap-2" data-testid="narrative-analyze">{busy ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Analyse the scenario (free)</Button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">{script.length}/8000 characters · analysis is free · building the theme costs 1 credit per 30 s track (reference + one per distinct state) · you have {user?.credits ?? "—"} credits</p>
      </section>

      <section className="space-y-5">
        {items.length === 0 && <p className="text-sm text-muted-foreground" data-testid="narrative-empty">No scenario analysed yet.</p>}
        {items.map((n) => (
          <div key={n.id} className="rounded-2xl border border-border bg-card p-5 space-y-4" data-testid={`narrative-card-${n.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <h3 className="font-display text-lg font-bold flex items-center gap-2">{n.title} <Badge variant="outline" className={n.status === "built" ? "text-emerald-400 border-emerald-500/30" : "text-primary border-primary/30"}>{n.status}</Badge></h3>
                <p className="text-xs text-muted-foreground mt-0.5">{n.summary}</p>
                <p className="text-[11px] font-mono text-muted-foreground mt-1">{n.plan.theme.genre}{n.genre2 ? ` × ${n.genre2}` : ""} · {n.plan.theme.mood} · {opts?.cultures[n.culture]?.label || n.culture} · {opts?.ensembles[n.ensemble]?.label || "auto"} · {n.plan.total_sec}s timeline · {n.provider}</p>
              </div>
              <div className="flex gap-1.5 flex-wrap">
                {n.theme_id ? (
                  <Button size="sm" variant="outline" className="gap-1.5" onClick={() => navigate("/dashboard/themes")} data-testid={`narrative-open-theme-${n.id}`}><Music4 size={14} /> Open theme <ArrowRight size={12} /></Button>
                ) : (
                  <Button size="sm" className="gap-1.5 bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => build(n)} data-testid={`narrative-build-${n.id}`}><Wand2 size={14} /> Build theme + cue sheet ({n.plan.credits} cr)</Button>
                )}
                <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-destructive" onClick={() => remove(n)} data-testid={`narrative-delete-${n.id}`}><Trash2 size={14} /></Button>
              </div>
            </div>
            <ScenePlan narrative={n} opts={opts} onChange={(patch) => update(n.id, patch)} readOnly={!!n.theme_id} />
          </div>
        ))}
      </section>

      <AgentsGrid />
    </div>
  );
}
